import {Injectable} from '@angular/core';
import {CalendarEvent, EventTypeToColor} from "../models/calendar-event";
import {MatDialog} from "@angular/material/dialog";
import {environment} from "@environments/environment";
import {UiManagerService} from "./ui-manager.service";
import {HttpClient} from "@angular/common/http";
import {RestApiManager} from "./rest-api-manager";
import {AccountManagerService} from "./account-manager-service";

@Injectable({
  providedIn: 'root'
})
export class EventManagerService {

  constructor(
    private accountManager: AccountManagerService,
    public dialog: MatDialog,
    private uiManagerService: UiManagerService,
    private httpClient: HttpClient,
    private restApiManager: RestApiManager,
  ) { }

  public async getEvent(eventId: string, onError?: Function): Promise<CalendarEvent> {
    const request = await this.restApiManager.get<CalendarEvent>(
      CalendarEvent,
      `${environment.restApi.endpoint}/events/${eventId}`,
      this.accountManager.account.authToken
    ).catch(e => e.status);
    if (!request) { return onError(); }
    return request;
  }

  public async getEvents(from: Date, to: Date): Promise<CalendarEvent[]> {
    return this.restApiManager.getAll<CalendarEvent>(
      CalendarEvent,
      `${environment.restApi.endpoint}/events/search_all`,
      this.accountManager.account.authToken,
      {
        params: { companyId: this.accountManager.account.company.id, from: from.getTime(), to: to.getTime() }
      }
    );
  }

  public async getEventsToDisplay(from: Date, to: Date) {
    const events = await this.getEvents(from, to);
    return events.map(event => {

      return {
        id: event.id,
        title: event.title,
        start: new Date(event.scheduledAt),
        end: event.endAt ? new Date(event.endAt) : null,
        color: EventTypeToColor(event.type),
        extra: {
          description: event.description,
        }
      }
    });
  }

  async createEvent(event: CalendarEvent): Promise<CalendarEvent> {
    return this.restApiManager.post<CalendarEvent>(
      `${environment.restApi.endpoint}/events`,
      event,
      this.accountManager.account.authToken
    );
  }

  async updateEvent(eventId: string, event: CalendarEvent): Promise<CalendarEvent> {
    return this.restApiManager.put<CalendarEvent>(
      `${environment.restApi.endpoint}/events/${eventId}`,
      event,
      this.accountManager.account.authToken
    );
  }

  async removeEvent(eventId: string): Promise<void> {
    return this.restApiManager.delete(
      `${environment.restApi.endpoint}/events/${eventId}`,
      this.accountManager.account.authToken
    );
  }

  async closeTraining(eventId: string, event: CalendarEvent) {

    // Set chat as closed
    // TODO protect with auth
    const tillyResponse = this.httpClient.post(
      `${environment.tilly.endpoint}/chat/ticket_${eventId}/room/100/close`,
      {}
    ).toPromise();

    // Update event close timestamp
    const eventResponse = this.updateEvent(eventId, {
      ...event,
      endAt: Date.now(),
      equipmentId: event.equipment.id,
      typeId: event.type,
      baseTrainingUrl: `${window.location.protocol}//${window.location.hostname}:${window.location.port}/training/`
    } as CalendarEvent);

    // Anonymous user cleanup
    // TODO protect with auth
    const uids = await this.httpClient.get<string[]>(
      `${environment.tilly.endpoint}/chat/ticket_${eventId}/room/100/getAnonymousUsers`,
    ).toPromise();

    // Clean users
    const usersCleanupResponse = this.restApiManager.deleteWithBody(
      `${environment.restApi.endpoint}/users/guests`,
      {
        "usersUids": uids
      },
      this.accountManager.account.authToken
    );

    return Promise.all([
      tillyResponse,
      eventResponse,
      usersCleanupResponse
    ])

  }

}
